import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Reveal from '@/components/feature/Reveal';
import MovieCard from '@/components/feature/MovieCard';
import HeroCarousel from '@/pages/home/components/HeroCarousel';
import { movies, getFeaturedMovies } from '@/mocks/movies';
import { categories } from '@/mocks/categories';
import { reviews } from '@/mocks/reviews';
import useSeo from '@/hooks/useSeo';

const stats = [
  { value: '1,280+', label: '收录影片', icon: 'ri-film-line' },
  { value: '360+', label: '专业影评', icon: 'ri-article-line' },
  { value: '24', label: '专题策划', icon: 'ri-book-marked-line' },
  { value: '18万', label: '影迷关注', icon: 'ri-group-line' },
];

export default function Home() {
  useSeo({
    title: '美国伦理片 - 精选美国伦理成人电影与影评网站',
    description:
      '美国伦理片网站专注于收录和介绍各类美国伦理题材电影，提供详尽的剧情分析和影视资讯，满足用户对美国伦理片电影的深入了解需求。',
    keywords: '美国伦理片,美国伦理电影,伦理电影推荐,美国情色电影,伦理电影剧情,伦理片影评,美国电影',
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);

  const featured = useMemo(() => getFeaturedMovies(), []);
  const latest = useMemo(
    () => [...movies].sort((a, b) => b.addedDate.localeCompare(a.addedDate)).slice(0, 8),
    [],
  );
  const editorPicks = useMemo(() => reviews.slice(0, 3), []);

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
          <span className="w-10 h-10 border-2 border-background-300 border-t-primary-500 rounded-full animate-spin" />
          <p className="text-sm text-foreground-500">正在加载精选内容…</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <HeroCarousel movies={featured} />

      {/* 分类快捷入口 */}
      <section className="px-4 md:px-8 py-12 md:py-16">
        <Reveal>
          <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">按题材快速浏览</h2>
              <p className="mt-2 text-sm text-foreground-500">从不同伦理视角切入，快速找到你想深入的内容方向</p>
            </div>
            <Link to="/category" className="text-sm font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer">
              查看全部分类 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
          {categories.map((c, i) => (
            <Reveal key={c.id} delay={i * 60}>
              <Link
                to={`/category?cat=${c.slug}`}
                className="group block h-full p-5 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 hover:-translate-y-1 transition-all duration-300 cursor-pointer"
              >
                <span className="w-12 h-12 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 mb-4 group-hover:bg-accent-500 group-hover:text-background-50 transition-colors">
                  <i className={`${c.icon} text-2xl`} />
                </span>
                <h3 className="text-base font-semibold text-foreground-950 mb-2">{c.name}</h3>
                <p className="text-xs leading-6 text-foreground-500 line-clamp-3">{c.description}</p>
                <p className="mt-3 text-xs text-secondary-700">收录 {c.count} 部 · 持续更新</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 最新上架 */}
      <section className="px-4 md:px-8 py-12 md:py-16 bg-background-50 border-y border-background-200">
        <Reveal>
          <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">最新上架影片</h2>
              <p className="mt-2 text-sm text-foreground-500">近期新增收录的伦理题材电影，涵盖剧情、社会与家庭议题</p>
            </div>
            <Link to="/category" className="text-sm font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer">
              浏览更多 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </Reveal>

        {latest.length === 0 ? (
          <p className="py-16 text-center text-foreground-500">暂无推荐内容</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {latest.map((m, i) => (
              <Reveal key={m.id} delay={i * 50}>
                <MovieCard movie={m} />
              </Reveal>
            ))}
          </div>
        )}
      </section>

      {/* 编辑精选影评 */}
      <section className="px-4 md:px-8 py-12 md:py-16">
        <Reveal>
          <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">编辑精选影评</h2>
              <p className="mt-2 text-sm text-foreground-500">来自编辑部的深度解读，带你看见剧情背后的伦理思考</p>
            </div>
            <Link to="/special" className="text-sm font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer">
              全部专题 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {editorPicks.map((r, i) => (
            <Reveal key={r.id} delay={i * 80}>
              <Link
                to={`/detail/${r.movieId}`}
                className="group flex flex-col h-full rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 hover:-translate-y-1 transition-all duration-300 cursor-pointer"
              >
                <div className="relative w-full h-44 overflow-hidden bg-background-200">
                  <img
                    src={r.cover}
                    alt={r.title}
                    title={`${r.title} - ${r.movieTitle}`}
                    className="w-full h-full object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <span className="absolute bottom-2.5 left-2.5 px-2.5 py-1 rounded-md bg-foreground-950/70 text-background-50 text-xs">
                    {r.movieTitle}
                  </span>
                </div>
                <div className="flex flex-col flex-1 p-5">
                  <h3 className="text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {r.title}
                  </h3>
                  <p className="text-sm leading-7 text-foreground-500 line-clamp-3 flex-1">{r.excerpt}</p>
                  <div className="mt-4 flex items-center gap-3 text-xs text-foreground-400">
                    <span className="flex items-center gap-1"><i className="ri-user-line" /> {r.author}</span>
                    <span className="flex items-center gap-1"><i className="ri-time-line" /> {r.readingTime} 分钟</span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 数据条 */}
      <section className="px-4 md:px-8 py-10 md:py-12 bg-primary-500">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((s) => (
            <div key={s.label} className="flex items-center gap-4">
              <span className="w-12 h-12 flex items-center justify-center rounded-md bg-background-50/15 text-background-50">
                <i className={`${s.icon} text-2xl`} />
              </span>
              <div>
                <p className="text-2xl md:text-3xl font-bold text-background-50">{s.value}</p>
                <p className="text-xs text-primary-100 mt-1">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 站点简介 */}
      <section className="px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <Reveal>
            <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium mb-4">
              关于本站
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 leading-snug">
              一个认真对待伦理题材的电影资料库
            </h2>
            <p className="text-sm md:text-base leading-8 text-foreground-600 mb-4">
              我们相信，伦理电影的价值不在于情节的刺激，而在于它敢于把观众放进一个无法轻易站队的处境里。本站以专业、克制的姿态，收录美国伦理题材电影，为每一部作品留下清晰的剧情脉络与真诚的评论。
            </p>
            <p className="text-sm md:text-base leading-8 text-foreground-600 mb-6">
              无论你是想快速了解一部影片讲了什么，还是希望读到更深入的剧情解析，都可以在这里找到入口。
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              了解我们的宗旨 <i className="ri-arrow-right-line" />
            </Link>
          </Reveal>
          <Reveal delay={120}>
            <div className="grid grid-cols-2 gap-4">
              <div className="w-full aspect-[4/5] rounded-lg overflow-hidden bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Cinematic%20editorial%20still%20of%20a%20dark%20movie%20theater%20with%20empty%20red%20seats%20and%20soft%20projector%20light%2C%20warm%20amber%20and%20deep%20indigo%20tones%2C%20atmospheric%20film%20grain%2C%20no%20text&width=600&height=750&seq=intro-about-01&orientation=portrait"
                  alt="影院放映氛围"
                  title="美国伦理片 - 观影氛围"
                  className="w-full h-full object-top"
                />
              </div>
              <div className="w-full aspect-[4/5] rounded-lg overflow-hidden bg-background-200 mt-8">
                <img
                  src="https://readdy.ai/api/search-image?query=Cinematic%20editorial%20still%20of%20an%20open%20notebook%20and%20film%20script%20pages%20under%20warm%20desk%20lamp%2C%20moody%20amber%20and%20charcoal%20tones%2C%20intimate%20writing%20mood%2C%20film%20grain%2C%20no%20text&width=600&height=750&seq=intro-about-02&orientation=portrait"
                  alt="影评手稿氛围"
                  title="美国伦理片 - 影评手稿"
                  className="w-full h-full object-top"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}